/**
 * FAZ 5 M3 — Cari Ödeme Al (Tahsilat) Dialog
 *
 * Backend: POST /api/customers/{customer_id}/payments
 *   payload: { amount, method, payment_date?, receipt_no?, notes?, order_id?, idempotency_key }
 * Response: Payment[] (auto-allocated across open orders or advance)
 *
 * - Tenant-scoped + permission-gated (finance:add) backend'de.
 * - Double-submit guard: `idempotency_key` her açılışta uuid ile üretilir.
 * - Kayıt sonrası onSaved() ile parent CustomerProfile refresh eder.
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Receipt } from "lucide-react";

const METHODS = [
  { value: "nakit", label: "Nakit" },
  { value: "havale", label: "Banka Havalesi / EFT" },
  { value: "kart", label: "Kredi Kartı" },
  { value: "cek", label: "Çek" },
  { value: "senet", label: "Senet" },
  { value: "diger", label: "Diğer" },
];

const _todayISO = () => new Date().toISOString().slice(0, 10);
const _uuid = () => (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

export function TakePaymentDialog({ customer, open, onOpenChange, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    amount: "",
    method: "havale",
    payment_date: _todayISO(),
    receipt_no: "",
    notes: "",
    order_id: "",
  });
  const [openOrders, setOpenOrders] = useState([]);
  const [idemKey, setIdemKey] = useState(_uuid());

  useEffect(() => {
    if (!open || !customer?.id) return;
    setIdemKey(_uuid());  // Her açılışta yeni key → çift submit'te de aynı key
    setForm((f) => ({ ...f, amount: "", receipt_no: "", notes: "", order_id: "" }));
    // Müşterinin açık siparişlerini listele — opsiyonel ilişkilendirme için
    api.get(`/finance/payments?customer_id=${customer.id}`).catch(() => {});  // warm cache
    api.get(`/orders?customer_id=${customer.id}&limit=100`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setOpenOrders(list.filter(o => (Number(o.total_price || 0) - Number(o.paid_amount || 0)) > 0.01));
      })
      .catch(() => setOpenOrders([]));
  }, [open, customer?.id]);

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Tutar 0'dan büyük olmalı");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        amount,
        method: form.method,
        payment_date: form.payment_date ? new Date(form.payment_date).toISOString() : undefined,
        receipt_no: form.receipt_no?.trim() || "",
        notes: form.notes?.trim() || "",
        order_id: form.order_id || "",
        idempotency_key: idemKey,
      };
      const { data } = await api.post(`/customers/${customer.id}/payments`, payload);
      const count = Array.isArray(data) ? data.length : 1;
      toast.success(`₺${amount.toLocaleString("tr-TR")} tahsilat kaydedildi (${count} kayıt)`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="take-payment-dialog" className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 tracking-tight">
            <Receipt className="w-4 h-4 text-emerald-500" />
            Ödeme Al / Tahsilat
          </DialogTitle>
          <p className="text-xs text-slate-500">
            Müşteri: <strong className="text-slate-300">{customer?.name}</strong>
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Tarih</Label>
              <Input
                type="date"
                data-testid="tp-date"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400">
                Tutar <span className="text-rose-400 ml-1">*</span>
              </Label>
              <Input
                type="number" step="0.01" min="0.01"
                data-testid="tp-amount"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                className="bg-slate-950 border-slate-700 rounded-sm font-mono"
                required
                autoFocus
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400">Ödeme Yöntemi</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger data-testid="tp-method" className="bg-slate-950 border-slate-700 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400">
              İlgili Sipariş (opsiyonel)
            </Label>
            <Select value={form.order_id || "auto"} onValueChange={(v) => setForm({ ...form, order_id: v === "auto" ? "" : v })}>
              <SelectTrigger data-testid="tp-order" className="bg-slate-950 border-slate-700 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                <SelectItem value="auto">— Otomatik (en eski açık siparişten başlayarak dağıt) —</SelectItem>
                {openOrders.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.order_no} · ₺{(Number(o.total_price || 0) - Number(o.paid_amount || 0)).toLocaleString("tr-TR")} kalan
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Belge / Ref No</Label>
              <Input
                data-testid="tp-receipt"
                value={form.receipt_no}
                onChange={(e) => setForm({ ...form, receipt_no: e.target.value })}
                placeholder="Opsiyonel"
                className="bg-slate-950 border-slate-700 rounded-sm font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Açıklama</Label>
              <Input
                data-testid="tp-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Opsiyonel"
                className="bg-slate-950 border-slate-700 rounded-sm"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
              İptal
            </Button>
            <Button type="submit" disabled={saving}
                    data-testid="tp-submit"
                    className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ödemeyi Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default TakePaymentDialog;
